import { useEffect, useState } from 'react';

import { SettingsOutlined } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { ApplicationAccessType, CommunicationType, ContentTypeForComparison } from 'domains/content/types';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { setApplicationsAccess, setCommunicationType } from 'pages/ContentVoiceBuilderPage/voiceCallActions';

import ApplicationsAccessOptionSelect from './ApplicationsAccessOptionSelect';
import CommunicationTypeOptionSelect from './CommunicationTypeOptionSelect';

const removeUnsubscribeLinkValidationMessage = 'This will remove unsubscribe link and this action cannot be undone';
const InCareValidationMessage = 'All InCare templates must be transactional';
const OutreachValidationMessage = 'All Outreach templates must be commercial';
const EmptyValueValidationMessage = 'Applications Access value cannot be empty';
export const DynamicContentValidationMessage =
    'Emails with dynamic content can only be used in the Outreach application';

const removeUnsubscribeLink = (beeJson: string) => {
    if (!beeJson) return beeJson;

    // Multiple patterns to catch different variations of unsubscribe links in BeeJSON
    // Note: In BeeJSON, quotes are escaped as \"
    const patterns = [
        // Pattern for the specific format found in the provided JSON
        '<a style=\\\\"color: #505050;\\\\" rel=\\\\"noopener\\\\" href=\\\\"\\[unsubscribe_link\\]\\\\">Unsubscribe</a>',
        // Pattern for other possible variations
        '<a[^>]*href=\\\\"\\[unsubscribe_link\\]\\\\"[^>]*>Unsubscribe</a>',
        // Original pattern (for backward compatibility)
        '<a href="\\[unsubscribe_link\\]"[^>]*>Unsubscribe</a>\\s*',
    ];

    let result = beeJson;
    patterns.forEach((pattern) => {
        const regex = new RegExp(pattern, 'gi');
        result = result.replace(regex, '');
    });

    return result;
};

const dialogTitle = 'Content Tagging';

type FormData = {
    communicationType: CommunicationType;
    applicationsAccess: ApplicationAccessType[];
};

type ContentTaggingButtonWithDialogProps = {
    beeJson?: string;
    dispatchVoiceContentAction?: React.Dispatch<any>;
    channel: ContentTypeForComparison;
    handleApplicationsAccess?: (event: any) => void;
    onBeeChangeFromCommunicationType?: (beeJson: string) => void;
    handleCommunicationType?: (event: any) => void;
    selectedApplicationsAccess: ApplicationAccessType[];
    selectedCommunicationType: CommunicationType;
    hasDynamicContent?: boolean;
};

const ContentTaggingButtonWithDialog = ({
    beeJson,
    dispatchVoiceContentAction,
    channel,
    handleApplicationsAccess,
    onBeeChangeFromCommunicationType,
    handleCommunicationType,
    selectedApplicationsAccess,
    selectedCommunicationType,
    hasDynamicContent = false,
}: ContentTaggingButtonWithDialogProps) => {
    const { data: featureSettings } = useFeatureSettings();
    const [isOpen, setIsOpen] = useState(false);
    const [removeUnsubscribeLinkMessage, setRemoveUnsubscribeLinkMessage] = useState('');
    const [validationErrorMessage, setValidationErrorMessage] = useState('');
    const isSaveDisabled = !!validationErrorMessage;

    const { control, handleSubmit, reset, watch } = useForm<FormData>({
        defaultValues: {
            communicationType: selectedCommunicationType,
            applicationsAccess: selectedApplicationsAccess,
        },
        reValidateMode: 'onChange',
    });

    const watchedCommunicationType = watch('communicationType');
    const watchedApplicationsAccess = watch('applicationsAccess');

    useEffect(() => {
        if (selectedCommunicationType && selectedApplicationsAccess) {
            reset({
                communicationType: selectedCommunicationType,
                applicationsAccess: selectedApplicationsAccess,
            });
        }
    }, [selectedCommunicationType, selectedApplicationsAccess, reset]);

    useEffect(() => {
        const hasTransactional = watchedCommunicationType === CommunicationType.TRANSACTIONAL;

        if (channel === ContentTypeForComparison.EMAIL && hasTransactional) {
            setRemoveUnsubscribeLinkMessage(removeUnsubscribeLinkValidationMessage);
        } else {
            setRemoveUnsubscribeLinkMessage('');
        }

        const hasInCare = watchedApplicationsAccess?.includes(ApplicationAccessType.INCARE);
        const hasOutreach = watchedApplicationsAccess?.includes(ApplicationAccessType.OUTREACH);
        const hasInJourney = watchedApplicationsAccess?.includes(ApplicationAccessType.INJOURNEY);
        const hasAccess = watchedApplicationsAccess?.includes(ApplicationAccessType.ACCESS);
        const hasNonOutreachApps = hasInCare || hasInJourney || hasAccess;

        if (watchedCommunicationType === CommunicationType.COMMERCIAL && hasInCare) {
            setValidationErrorMessage(InCareValidationMessage);
        } else if (watchedCommunicationType === CommunicationType.TRANSACTIONAL && hasOutreach) {
            setValidationErrorMessage(OutreachValidationMessage);
        } else if (channel === ContentTypeForComparison.EMAIL && hasDynamicContent && hasNonOutreachApps) {
            setValidationErrorMessage(DynamicContentValidationMessage);
        } else if (watchedApplicationsAccess?.length === 0) {
            setValidationErrorMessage(EmptyValueValidationMessage);
        } else {
            setValidationErrorMessage('');
        }
    }, [watchedCommunicationType, watchedApplicationsAccess, channel, hasDynamicContent]);

    const onSubmit = (data: FormData) => {
        if (channel === ContentTypeForComparison.VOICE) {
            if (data.communicationType) {
                dispatchVoiceContentAction(setCommunicationType(data.communicationType));
            }
            if (data.applicationsAccess) {
                dispatchVoiceContentAction(setApplicationsAccess(data.applicationsAccess));
            }
        } else {
            if (data.communicationType) {
                handleCommunicationType({ target: { value: data.communicationType } });
            }
            if (data.applicationsAccess) {
                handleApplicationsAccess({ target: { value: data.applicationsAccess } });
            }
        }

        if (channel === ContentTypeForComparison.EMAIL && data.communicationType === CommunicationType.TRANSACTIONAL) {
            onBeeChangeFromCommunicationType(removeUnsubscribeLink(beeJson));
        }
        setIsOpen(false);
    };

    const handleCancel = () => {
        reset({
            communicationType: selectedCommunicationType,
            applicationsAccess: selectedApplicationsAccess,
        });
        setIsOpen(false);
    };

    return (
        <>
            {featureSettings?.platformIntegrated && (
                <Tooltip title="Content Tagging" placement="top">
                    <IconButton
                        aria-label="Content Tagging"
                        size="large"
                        sx={{ paddingLeft: 0 }}
                        onClick={() => setIsOpen(true)}
                    >
                        <SettingsOutlined />
                    </IconButton>
                </Tooltip>
            )}
            <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={handleCancel}>
                <DialogTitle id="content-tagging-dialog">
                    <Typography alignSelf={'center'} variant="h6">
                        {dialogTitle}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack sx={{ py: 1 }} rowGap={2}>
                            <Controller
                                name="communicationType"
                                control={control}
                                render={({ field }) => (
                                    <CommunicationTypeOptionSelect
                                        selectedCommunicationType={field.value}
                                        handleCommunicationTypeSelect={(event: any) =>
                                            field.onChange(event.target.value)
                                        }
                                        removeUnsubscribeLinkMessage={removeUnsubscribeLinkMessage}
                                    />
                                )}
                            />

                            <Controller
                                name="applicationsAccess"
                                control={control}
                                render={({ field }) => (
                                    <ApplicationsAccessOptionSelect
                                        selectedApplicationsAccess={field.value}
                                        validationErrorMessage={validationErrorMessage}
                                        handleApplicationsAccessSelect={(value: string[]) => field.onChange(value)}
                                        hasDynamicContent={hasDynamicContent}
                                        channel={channel}
                                    />
                                )}
                            />
                        </Stack>
                    </form>
                    <DialogActions sx={{ px: 0, py: 2 }}>
                        <Button variant="text" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSaveDisabled}>
                            Save
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ContentTaggingButtonWithDialog;
